/**
 * トリガーシステム: トリガーエンジン
 *
 * トリガーの登録、管理、発火を担当する中核システム
 *
 * 主要機能:
 * 1. グローバルトリガーレジストリの管理
 * 2. カードのトリガー登録・解除
 * 3. 自動発動トリガーの発火
 * 4. 任意発動トリガーの取得とUI表示サポート
 * 5. トリガー条件のチェックと優先度制御
 *
 * @see src/ルール/trigger-revision-plan.md - 設計詳細
 * @see src/engine/triggerTypes.ts - トリガータイプ定義
 */

import {
  TRIGGER_TYPES,
  ACTIVATION_TYPES,
  TRIGGER_PRIORITIES,
  isOptionalTrigger,
  type TriggerTypeValue,
  type ActivationTypeValue,
  type TriggerPriorityValue,
} from './triggerTypes';
import { getFutureCardTriggers, hasFutureCardTrigger } from './cardTriggers/futureCards';
import { getFireCardTriggers, hasFireCardTrigger } from './cardTriggers/fireCards';
import { getWaterCardTriggers, hasWaterCardTrigger } from './cardTriggers/waterCards';
import { getLightCardTriggers, hasLightCardTrigger } from './cardTriggers/lightCards';
import { getDarkCardTriggers, hasDarkCardTrigger } from './cardTriggers/darkCards';
import { getPrimitiveCardTriggers, hasPrimitiveCardTrigger } from './cardTriggers/primitiveCards';
import { getNeutralCardTriggers, hasNeutralCardTrigger } from './cardTriggers/neutralCards';
import { getEffectDefinition } from './continuousEffects';
import type { CardData, EffectContext, TriggerDefinition, PlayerNumber, MonsterInstance } from '../types';

/** トリガーエントリー */
interface TriggerEntry {
  cardId: string;
  triggerType: TriggerTypeValue;
  cardName: string;
  owner: PlayerNumber;
  slotIndex: number | null;
  priority: TriggerPriorityValue;
  activationType: ActivationTypeValue;
  condition?: (context: TriggerContext) => boolean;
  effect?: (context: TriggerContext) => void;
  description?: string;
  costCheck?: (context: TriggerContext) => boolean;
  usedThisTurn: boolean;
  targetSelf?: boolean;
}

/** トリガーコンテキスト */
export interface TriggerContext extends Partial<EffectContext> {
  currentPlayer: PlayerNumber;
  card?: CardData | MonsterInstance;
  destroyedCard?: CardData | MonsterInstance;
  leavingCard?: CardData | MonsterInstance;
  leaveReason?: string;
  slotIndex?: number;
  attackerIndex?: number;
  targetIndex?: number;
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  /** 攻撃時トリガーで蓄積されるダメージボーナス（React state更新の非同期問題を回避） */
  accumulatedDamageBonus?: number;
  [key: string]: any;
}

/** パースされたトリガー */
interface ParsedTrigger {
  type: TriggerTypeValue;
  activationType?: ActivationTypeValue;
  priority?: TriggerPriorityValue;
  description?: string;
  condition?: (context: TriggerContext) => boolean;
  effect?: (context: TriggerContext) => void;
  costCheck?: (context: TriggerContext) => boolean;
}

/**
 * グローバルトリガーレジストリ
 * トリガータイプごとにトリガーエントリーを管理
 */
class TriggerRegistry {
  triggers: Map<TriggerTypeValue, TriggerEntry[]>;

  constructor() {
    // Map<triggerType, Array<TriggerEntry>>
    this.triggers = new Map();

    // 全トリガータイプを初期化
    Object.values(TRIGGER_TYPES).forEach((type) => {
      this.triggers.set(type, []);
    });
  }

  /**
   * トリガーを登録
   * @param cardId - カードの一意なID
   * @param triggerType - トリガータイプ
   * @param triggerData - トリガーデータ
   */
  register(cardId: string, triggerType: TriggerTypeValue, triggerData: Omit<TriggerEntry, 'cardId' | 'triggerType' | 'usedThisTurn'>): void {
    if (!this.triggers.has(triggerType)) {
      console.warn(`Unknown trigger type: ${triggerType}`);
      return;
    }

    const triggerArray = this.triggers.get(triggerType)!;

    // 同じカードで同じトリガータイプ・同じactivationTypeの重複のみ禁止
    // （同じカードで同じトリガータイプでもactivationTypeが異なれば共存可能）
    const existingIndex = triggerArray.findIndex((t) =>
      t.cardId === cardId && t.activationType === triggerData.activationType
    );
    if (existingIndex !== -1) {
      // 既存のトリガーを上書き
      triggerArray[existingIndex] = {
        cardId,
        triggerType,
        ...triggerData,
        usedThisTurn: false,
      };
    } else {
      // 新規登録
      triggerArray.push({
        cardId,
        triggerType,
        ...triggerData,
        usedThisTurn: false,
      });
    }
  }

  /**
   * 特定のカードのトリガーをすべて解除
   * @param cardId - カードの一意なID
   */
  unregister(cardId: string): void {
    this.triggers.forEach((triggerArray, triggerType) => {
      const filtered = triggerArray.filter((t) => t.cardId !== cardId);
      this.triggers.set(triggerType, filtered);
    });
  }

  /**
   * 特定のトリガータイプのトリガーをすべて取得
   * @param triggerType - トリガータイプ
   * @returns トリガーエントリーの配列
   */
  get(triggerType: TriggerTypeValue): TriggerEntry[] {
    return this.triggers.get(triggerType) || [];
  }

  /**
   * 特定のカードのトリガーを取得
   * @param cardId - カードの一意なID
   * @returns トリガーエントリーの配列
   */
  getByCardId(cardId: string): TriggerEntry[] {
    const result: TriggerEntry[] = [];
    this.triggers.forEach((triggerArray) => {
      triggerArray.forEach((trigger) => {
        if (trigger.cardId === cardId) {
          result.push(trigger);
        }
      });
    });
    return result;
  }

  /**
   * すべてのトリガーをクリア（ゲームリセット用）
   */
  clear(): void {
    this.triggers.forEach((_, triggerType) => {
      this.triggers.set(triggerType, []);
    });
  }

  /**
   * デバッグ用: 登録されているトリガーの統計
   */
  getStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    this.triggers.forEach((triggerArray, triggerType) => {
      if (triggerArray.length > 0) {
        stats[triggerType] = triggerArray.length;
      }
    });
    return stats;
  }
}

// グローバルレジストリのインスタンス
export const globalRegistry = new TriggerRegistry();

// ========================================
// トリガー登録・解除
// ========================================

/**
 * カードのトリガーを解析して登録
 * @param card - カードオブジェクト
 * @param owner - オーナープレイヤー (1 or 2)
 * @param slotIndex - フィールドスロット (null = 墓地)
 * @returns 登録されたトリガーの数
 */
export const registerCardTriggers = (card: CardData & { uniqueId?: string }, owner: PlayerNumber, slotIndex: number | null = null): number => {
  if (!card || !card.uniqueId) {
    console.warn('Invalid card for trigger registration:', card);
    return 0;
  }

  // カード固有のトリガーを解析
  const triggers = parseCardTriggers(card);

  let registeredCount = 0;

  triggers.forEach((trigger) => {
    globalRegistry.register(card.uniqueId!, trigger.type, {
      cardName: card.name,
      owner,
      slotIndex,
      priority: trigger.priority || TRIGGER_PRIORITIES.NORMAL,
      activationType: trigger.activationType || ACTIVATION_TYPES.AUTOMATIC,
      condition: trigger.condition,
      effect: trigger.effect,
      description: trigger.description,
      costCheck: trigger.costCheck,
    });

    registeredCount++;
  });

  return registeredCount;
};

/**
 * カードの効果テキストからトリガーを解析
 * @param card - カードオブジェクト
 * @returns トリガー定義の配列
 */
export const parseCardTriggers = (card: CardData): ParsedTrigger[] => {
  const triggers: ParsedTrigger[] = [];

  if (!card || !card.effect) {
    return triggers;
  }

  // カード固有のトリガー実装をチェック
  // 未来属性
  if (card.id && hasFutureCardTrigger(card.id)) {
    const cardSpecificTriggers = getFutureCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 炎属性
  if (card.id && hasFireCardTrigger(card.id)) {
    const cardSpecificTriggers = getFireCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 水属性
  if (card.id && hasWaterCardTrigger(card.id)) {
    const cardSpecificTriggers = getWaterCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 光属性
  if (card.id && hasLightCardTrigger(card.id)) {
    const cardSpecificTriggers = getLightCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 闇属性
  if (card.id && hasDarkCardTrigger(card.id)) {
    const cardSpecificTriggers = getDarkCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 原始属性
  if (card.id && hasPrimitiveCardTrigger(card.id)) {
    const cardSpecificTriggers = getPrimitiveCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // 属性なし
  if (card.id && hasNeutralCardTrigger(card.id)) {
    const cardSpecificTriggers = getNeutralCardTriggers(card.id);
    if (cardSpecificTriggers) {
      return cardSpecificTriggers as unknown as ParsedTrigger[];
    }
  }

  // カード固有の実装がない場合、汎用パターンマッチングにフォールバック
  const effectText = card.effect;

  // 【召喚時】パターン
  if (effectText.includes('【召喚時】') || effectText.includes('【このカードの召喚時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時効果',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}の召喚時効果を発動！`, 'info');
      },
    });
  }

  // 【破壊時】【自壊時】パターン
  if (effectText.includes('【破壊時】') || effectText.includes('【自壊時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時効果',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}の破壊時効果を発動！`, 'info');
      },
    });
  }

  // 【場を離れる時】パターン
  if (effectText.includes('【場を離れる時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_LEAVE_FIELD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '場を離れる時効果',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}の場を離れる時効果を発動！`, 'info');
      },
    });
  }

  // 【自分メインフェイズ時】パターン
  if (effectText.includes('【自分メインフェイズ時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL, // 任意発動
      description: 'メインフェイズ時効果',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}のメインフェイズ効果を発動！`, 'info');
      },
    });
  }

  // 【自分エンドフェイズ時】パターン
  if (effectText.includes('【自分エンドフェイズ時】') || effectText.includes('【エンドフェイズ時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時効果',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}のエンドフェイズ効果を発動！`, 'info');
      },
    });
  }

  // 【常時】パターン
  if (effectText.includes('【常時】')) {
    // continuousEffects の定義から description を取得
    let description = '常時効果';
    if (card.id) {
      const effectDef = getEffectDefinition(card.id);
      if (effectDef && effectDef.length > 0 && effectDef[0].description) {
        description = effectDef[0].description;
      }
    }

    triggers.push({
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: description,
      effect: (context: TriggerContext) => {
        // 常時効果は別の仕組みで処理する予定
        // context.addLog(`${card.name}の常時効果が有効！`, 'info');
      },
    });
  }

  // 【墓地発動】パターン - メインフェイズ時
  // 「墓地にある」「墓地で発動」などのパターンで、SPコストを払う場合はメインフェイズ
  if (effectText.includes('墓地にある') && effectText.includes('SP')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '墓地発動効果（メインフェイズ）',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}の墓地効果を発動！`, 'info');
      },
    });
  }

  // 【墓地発動】パターン - エンドフェイズ時
  // 「墓地にある」「ターン終了時」などのパターン
  if (effectText.includes('墓地にある') && effectText.includes('ターン終了時')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地発動効果（エンドフェイズ）',
      effect: (context: TriggerContext) => {
        context.addLog(`${card.name}の墓地効果を発動！`, 'info');
      },
    });
  }

  return triggers;
};

/**
 * カードのトリガーをすべて解除
 * @param cardId - カードの一意なID
 */
export const unregisterCardTriggers = (cardId: string): void => {
  globalRegistry.unregister(cardId);
};

/**
 * すべてのトリガーをクリア（ゲームリセット用）
 */
export const clearAllTriggers = (): void => {
  globalRegistry.clear();
};

// ========================================
// 自動発動トリガーの発火
// ========================================

/**
 * 「場を離れる時」トリガーを発火
 * 破壊、手札戻し、デッキ戻し、除外など全てで発動
 * @param card - 場を離れるカード
 * @param context - ゲームコンテキスト
 * @param reason - 離れる理由 ('destroy', 'return_to_hand', 'return_to_deck', 'exile')
 */
export const fireLeaveFieldTrigger = (card: CardData & { uniqueId?: string }, context: TriggerContext, reason: string = 'destroy'): void => {
  if (!card || !card.uniqueId) {
    return;
  }

  const triggers = globalRegistry.get(TRIGGER_TYPES.ON_LEAVE_FIELD);

  if (!triggers || triggers.length === 0) {
    return;
  }

  // このカードのトリガーのみをフィルター
  const cardTriggers = triggers.filter((trigger) => {
    return trigger.cardId === card.uniqueId && trigger.activationType === ACTIVATION_TYPES.AUTOMATIC;
  });

  if (cardTriggers.length === 0) {
    return;
  }

  // コンテキストに離れる理由を追加
  const leaveContext: TriggerContext = {
    ...context,
    leavingCard: card,
    leaveReason: reason,
  };

  // 各トリガーを実行
  cardTriggers.forEach((trigger) => {
    try {
      // 条件チェック
      if (trigger.condition && !trigger.condition(leaveContext)) {
        return;
      }

      // 効果実行
      if (typeof trigger.effect === 'function') {
        trigger.effect(leaveContext);
      }
    } catch (error) {
      console.error(`場を離れるトリガー実行エラー: ${trigger.cardName}`, error);
      context.addLog(`${trigger.cardName}の効果実行中にエラーが発生しました`, 'damage');
    }
  });
};

/**
 * 特定のトリガータイプを発火（自動発動のみ）
 * @param triggerType - トリガータイプ
 * @param context - ゲームコンテキスト
 */
export const fireTrigger = (triggerType: TriggerTypeValue, context: TriggerContext): void => {
  const triggers = globalRegistry.get(triggerType);

  if (!triggers || triggers.length === 0) {
    return;
  }

  // 自動発動のみをフィルター
  let automaticTriggers = triggers.filter((trigger) => {
    return trigger.activationType === ACTIVATION_TYPES.AUTOMATIC;
  });

  // ON_SUMMONトリガーは召喚されたカード自身のトリガーのみ発火
  // （「このカードが召喚された時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_SUMMON && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_DESTROY_SELFトリガーは破壊されたカード自身のトリガーのみ発火
  // （「このカードが破壊された時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_DESTROY_SELF && context.destroyedCard) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.destroyedCard as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_PHASE_CARD_ACTIVATEトリガーは設置されたカード自身のトリガーのみ発火
  // （「このフェイズカードの発動時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_PHASE_CARD_SETトリガーは設置されたカード自身のトリガーのみ発火
  // （「このフェイズカード設置時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_PHASE_CARD_SET && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_FIELD_CARD_SETトリガーは設置されたカード自身のトリガーのみ発火
  // （「このフィールドカード設置時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_FIELD_CARD_SET && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_SURFACEトリガーは浮上したカード自身のトリガーのみ発火
  // （「このカードが浮上した時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_SURFACE && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_SUBMERGEトリガーは潜航したカード自身のトリガーのみ発火
  // （「このカードが潜航した時」という意味のため）
  if (triggerType === TRIGGER_TYPES.ON_SUBMERGE && context.card) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // フェイズ関連の「_SELF」トリガーはオーナーがターンプレイヤーの場合のみ発動
  // （「自分のエンドフェイズ時」= カードオーナーのターンのエンドフェイズ時）
  const phaseSelfTriggers: TriggerTypeValue[] = [
    TRIGGER_TYPES.ON_TURN_START_SELF,
    TRIGGER_TYPES.ON_DRAW_PHASE_SELF,
    TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
    TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF,
    TRIGGER_TYPES.ON_END_PHASE_SELF,
  ];

  if (phaseSelfTriggers.includes(triggerType)) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === context.currentPlayer;
    });
  }

  // フェイズ関連の「_OPPONENT」トリガーはオーナーがターンプレイヤーと異なる場合のみ発動
  // （「相手のエンドフェイズ時」= 相手ターンのエンドフェイズ時）
  const phaseOpponentTriggers: TriggerTypeValue[] = [
    TRIGGER_TYPES.ON_OPPONENT_END_PHASE,
  ];

  if (phaseOpponentTriggers.includes(triggerType)) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner !== context.currentPlayer;
    });
  }

  // ON_ATTACK: 【自攻撃時】このモンスター自身が攻撃した時のみ発火
  // targetSelf: false の場合は自分の全モンスターの攻撃で発火（【自分の〇〇モンスター攻撃時】用）
  if (triggerType === TRIGGER_TYPES.ON_ATTACK) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      if (trigger.owner !== context.currentPlayer) return false;
      // targetSelf: false の場合は自分の全モンスターの攻撃で発火
      if (trigger.targetSelf === false) return true;
      // デフォルト（targetSelf未指定 or true）は自身の攻撃のみ
      return trigger.slotIndex === context.attackerIndex;
    });
  }

  // ON_ATTACKED: 【被攻撃時】このモンスター自身が攻撃された時のみ発火
  // 攻撃対象自身のトリガーのみ発火
  // attackedContextでcurrentPlayerは被攻撃者に切り替わっているので、===で自分のトリガーを発火
  if (triggerType === TRIGGER_TYPES.ON_ATTACKED) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === context.currentPlayer &&
             trigger.slotIndex === context.targetIndex;
    });
  }

  // ON_OPPONENT_ATTACK: 【相手モンスター攻撃時】相手のモンスターが攻撃した時
  // 自分のカードのトリガーが発火（フィールドカード等の効果用）
  // attackedContextでcurrentPlayerは被攻撃者に切り替わっているので、===で自分のトリガーを発火
  if (triggerType === TRIGGER_TYPES.ON_OPPONENT_ATTACK) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === context.currentPlayer;
    });
  }

  // ON_ATTACK_SUCCESS: 【攻撃成功時】このモンスター自身が攻撃で相手モンスターを破壊した時のみ発火
  // 攻撃者のオーナーとスロットインデックスが一致するトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_ATTACK_SUCCESS) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === context.currentPlayer &&
             trigger.slotIndex === context.slotIndex;
    });
  }

  if (automaticTriggers.length === 0) {
    return;
  }

  // 優先度順にソート
  const sortedTriggers = sortTriggersByPriority(automaticTriggers, context);

  // 各トリガーを実行
  sortedTriggers.forEach((trigger) => {
    try {
      // トリガーを所有するカードをフィールドから検索してコンテキストに追加
      const ownerField = trigger.owner === 1 ? context.p1Field : context.p2Field;
      const triggerCard = ownerField
        ? ownerField.find((m) => m && m.uniqueId === trigger.cardId)
        : null;

      // トリガー実行用のコンテキストを作成（カード情報を追加）
      // フィールドにカードが見つかった場合のみ上書き、見つからない場合は元のcontext.cardを維持
      // （ON_DESTROY_SELFなど、カードがすでにフィールドにない場合に対応）
      // currentPlayerをトリガーのオーナーに切り替え（ON_OPPONENT_END_PHASE等で正しいフィールドを参照するため）
      const triggerContext: TriggerContext = {
        ...context,
        currentPlayer: trigger.owner,
        card: triggerCard || context.card || context.destroyedCard,
        slotIndex: trigger.slotIndex ?? context.slotIndex,
      };

      // 条件チェック
      if (trigger.condition && !trigger.condition(triggerContext)) {
        return; // 条件を満たさない
      }

      // 効果実行
      if (typeof trigger.effect === 'function') {
        trigger.effect(triggerContext);
      }

      // ログ出力（effectから出力されていない場合）
      // context.addLog(`${trigger.cardName}の効果を発動！`, 'info');
    } catch (error) {
      console.error(`トリガー実行エラー: ${trigger.cardName}`, error);
      context.addLog(`${trigger.cardName}の効果実行中にエラーが発生しました`, 'damage');
    }
  });
};

/**
 * 特定のトリガータイプのOPTIONALトリガーを発火
 * ON_PHASE_CARD_SETなど、設置時に任意効果を発動する場合に使用
 * @param triggerType - トリガータイプ
 * @param context - ゲームコンテキスト
 */
export const fireOptionalTriggers = (triggerType: TriggerTypeValue, context: TriggerContext): void => {
  const triggers = globalRegistry.get(triggerType);

  if (!triggers || triggers.length === 0) {
    return;
  }

  // OPTIONALトリガーのみをフィルター
  let optionalTriggers = triggers.filter((trigger) => {
    return trigger.activationType === ACTIVATION_TYPES.OPTIONAL;
  });

  // ON_PHASE_CARD_SETトリガーは設置されたカード自身のトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_PHASE_CARD_SET && context.card) {
    optionalTriggers = optionalTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_PHASE_CARD_ACTIVATEトリガーも同様
  if (triggerType === TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE && context.card) {
    optionalTriggers = optionalTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  // ON_FIELD_CARD_SETトリガーは設置されたカード自身のトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_FIELD_CARD_SET && context.card) {
    optionalTriggers = optionalTriggers.filter((trigger) => {
      return trigger.cardId === (context.card as CardData & { uniqueId?: string }).uniqueId;
    });
  }

  if (optionalTriggers.length === 0) {
    return;
  }

  // 優先度順にソート
  const sortedTriggers = sortTriggersByPriority(optionalTriggers, context);

  // 各トリガーを実行
  sortedTriggers.forEach((trigger) => {
    try {
      const triggerContext: TriggerContext = {
        ...context,
        currentPlayer: trigger.owner,
        card: context.card,
        slotIndex: trigger.slotIndex ?? context.slotIndex,
      };

      // 条件チェック
      if (trigger.condition && !trigger.condition(triggerContext)) {
        return;
      }

      // 効果実行（UIを表示するなどの処理）
      if (typeof trigger.effect === 'function') {
        trigger.effect(triggerContext);
      }
    } catch (error) {
      console.error(`OPTIONALトリガー実行エラー: ${trigger.cardName}`, error);
      context.addLog(`${trigger.cardName}の効果実行中にエラーが発生しました`, 'damage');
    }
  });
};

/**
 * トリガーを優先度順にソート
 * @param triggers - トリガーエントリーの配列
 * @param context - ゲームコンテキスト
 * @returns ソート済みのトリガー配列
 */
const sortTriggersByPriority = (triggers: TriggerEntry[], context: TriggerContext): TriggerEntry[] => {
  return [...triggers].sort((a, b) => {
    // 優先度が高い順（降順）
    if (b.priority !== a.priority) {
      return b.priority - a.priority;
    }

    // 優先度が同じ場合、オーナーがターンプレイヤーのトリガーを先に
    const currentPlayer = context.currentPlayer;
    if (a.owner === currentPlayer && b.owner !== currentPlayer) {
      return -1;
    }
    if (b.owner === currentPlayer && a.owner !== currentPlayer) {
      return 1;
    }

    // それ以外は登録順
    return 0;
  });
};

// ========================================
// 任意発動トリガーの取得（UI表示用）
// ========================================

/** 発動可能トリガー */
interface ActivatableTrigger extends TriggerEntry {
  canActivate: boolean;
}

/**
 * フィールドカードのメインフェイズトリガーを取得
 * @param card - フィールドのカードオブジェクト
 * @param currentPlayer - 現在のプレイヤー
 * @returns 発動可能なトリガーの配列
 */
export const getCardMainPhaseTriggers = (card: CardData & { uniqueId?: string }, currentPlayer: PlayerNumber): ActivatableTrigger[] => {
  if (!card || !card.uniqueId) {
    return [];
  }

  const triggers = globalRegistry.get(TRIGGER_TYPES.ON_MAIN_PHASE_SELF);

  return triggers
    .filter((trigger) => {
      // このカードのトリガーかつ、オーナーがターンプレイヤー
      return trigger.cardId === card.uniqueId && trigger.owner === currentPlayer;
    })
    .map((trigger) => ({
      ...trigger,
      canActivate: !trigger.usedThisTurn,
    }));
};

/**
 * 墓地カードのトリガーを取得
 * @param card - 墓地のカードオブジェクト
 * @param context - ゲームコンテキスト
 * @returns 発動可能なトリガーの配列
 */
export const getCardGraveyardTriggers = (card: CardData, context: TriggerContext): ActivatableTrigger[] => {
  if (!card || !card.effect) {
    return [];
  }

  // 墓地のカードはレジストリに登録されていないので、効果テキストを直接パース
  const triggers = parseCardTriggers(card);

  return triggers
    .filter((trigger) => {
      // 墓地発動トリガーのみ
      return (
        trigger.type === TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD ||
        trigger.type === TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD
      );
    })
    .map((trigger) => ({
      cardId: (card as CardData & { uniqueId?: string }).uniqueId || card.id,
      triggerType: trigger.type,
      cardName: card.name,
      owner: context.currentPlayer,
      slotIndex: null,
      priority: trigger.priority || TRIGGER_PRIORITIES.NORMAL,
      activationType: trigger.activationType || ACTIVATION_TYPES.OPTIONAL,
      condition: trigger.condition,
      effect: trigger.effect,
      description: trigger.description,
      costCheck: trigger.costCheck,
      usedThisTurn: false,
      canActivate: checkGraveyardTriggerActivatable(card, trigger, context),
    }));
};

/**
 * 墓地トリガーが発動可能かチェック
 * @param card - 墓地のカードオブジェクト
 * @param trigger - トリガーオブジェクト
 * @param context - ゲームコンテキスト
 * @returns 発動可能な場合true
 */
const checkGraveyardTriggerActivatable = (card: CardData, trigger: ParsedTrigger, context: TriggerContext): boolean => {
  // 1ターンに1度制限のチェック
  // Note: 墓地カードはusedThisTurnを持たないので、別途管理が必要

  // コストチェック
  if (trigger.costCheck && !trigger.costCheck(context)) {
    return false;
  }

  // 条件チェック
  if (trigger.condition && !trigger.condition(context)) {
    return false;
  }

  return true;
};

// ========================================
// トリガーの手動発動
// ========================================

/**
 * トリガーを手動で発動（任意発動用）
 * @param trigger - トリガーオブジェクト
 * @param context - ゲームコンテキスト
 * @returns 発動成功の場合true
 */
export const activateTrigger = (trigger: TriggerEntry, context: TriggerContext): boolean => {
  try {
    // トリガーを所有するカードをフィールドから検索してコンテキストに追加
    const ownerField = trigger.owner === 1 ? context.p1Field : context.p2Field;
    const triggerCard = ownerField
      ? ownerField.find((m) => m && m.uniqueId === trigger.cardId)
      : null;

    // トリガー実行用のコンテキストを作成（カード情報を追加）
    // フィールドにカードが見つかった場合のみ上書き、見つからない場合は元のcontext.cardを維持
    const triggerContext: TriggerContext = {
      ...context,
      card: triggerCard || context.card || context.destroyedCard,
      slotIndex: trigger.slotIndex ?? context.slotIndex,
    };

    // 発動可能かチェック
    if (trigger.usedThisTurn) {
      context.addLog(`${trigger.cardName}は既に使用済みです`, 'info');
      return false;
    }

    // コストチェック
    if (trigger.costCheck && !trigger.costCheck(triggerContext)) {
      context.addLog(`${trigger.cardName}を発動できません（コスト不足）`, 'info');
      return false;
    }

    // 条件チェック
    if (trigger.condition && !trigger.condition(triggerContext)) {
      context.addLog(`${trigger.cardName}を発動できません（条件不足）`, 'info');
      return false;
    }

    // 使用済みフラグを設定
    trigger.usedThisTurn = true;

    // 効果実行
    if (typeof trigger.effect === 'function') {
      trigger.effect(triggerContext);
    }

    // ログ出力
    context.addLog(`${trigger.cardName}の効果を発動！`, 'info');

    return true;
  } catch (error) {
    console.error(`トリガー発動エラー: ${trigger.cardName}`, error);
    context.addLog(`${trigger.cardName}の効果実行中にエラーが発生しました`, 'damage');
    return false;
  }
};

/**
 * トリガー条件をチェック
 * @param trigger - トリガーオブジェクト
 * @param context - ゲームコンテキスト（オプション）
 * @returns 条件を満たす場合true
 */
export const checkTriggerCondition = (trigger: TriggerEntry, context: TriggerContext | null = null): boolean => {
  if (!trigger.condition) {
    return true; // 条件なしは常にtrue
  }

  if (context && typeof trigger.condition === 'function') {
    try {
      return trigger.condition(context);
    } catch (error) {
      console.error('トリガー条件チェックエラー:', error);
      return false;
    }
  }

  return true;
};

// ========================================
// ターン管理
// ========================================

/**
 * ターン終了時に使用済みフラグをリセット
 */
export const resetTurnFlags = (): void => {
  globalRegistry.triggers.forEach((triggerArray) => {
    triggerArray.forEach((trigger) => {
      trigger.usedThisTurn = false;
    });
  });
};

// ========================================
// デバッグ・ユーティリティ
// ========================================

/**
 * 登録されているトリガーの統計を取得
 * @returns トリガータイプごとの登録数
 */
export const getTriggerStats = (): Record<string, number> => {
  return globalRegistry.getStats();
};

/**
 * 特定のカードの登録トリガーを取得
 * @param cardId - カードの一意なID
 * @returns トリガーエントリーの配列
 */
export const getCardTriggers = (cardId: string): TriggerEntry[] => {
  return globalRegistry.getByCardId(cardId);
};

/**
 * カードがトリガー実装を持っているかチェック
 * @param cardId - カードID (例: 'C0000085')
 * @param triggerType - トリガータイプ (省略時は召喚時をチェック)
 * @returns トリガー実装を持っている場合true
 */
export const hasCardTrigger = (cardId: string, triggerType: TriggerTypeValue = TRIGGER_TYPES.ON_SUMMON): boolean => {
  if (!cardId) return false;

  // 未来属性カードのトリガーをチェック
  if (hasFutureCardTrigger(cardId)) {
    const triggers = getFutureCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      // 指定されたトリガータイプが含まれているかチェック
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 炎属性カードのトリガーをチェック
  if (hasFireCardTrigger(cardId)) {
    const triggers = getFireCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 水属性カードのトリガーをチェック
  if (hasWaterCardTrigger(cardId)) {
    const triggers = getWaterCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 光属性カードのトリガーをチェック
  if (hasLightCardTrigger(cardId)) {
    const triggers = getLightCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 闘属性カードのトリガーをチェック
  if (hasDarkCardTrigger(cardId)) {
    const triggers = getDarkCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 原始属性カードのトリガーをチェック
  if (hasPrimitiveCardTrigger(cardId)) {
    const triggers = getPrimitiveCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  // 属性なしカードのトリガーをチェック
  if (hasNeutralCardTrigger(cardId)) {
    const triggers = getNeutralCardTriggers(cardId);
    if (triggers && Array.isArray(triggers)) {
      return triggers.some(trigger => (trigger.type as string) === (triggerType as string));
    }
  }

  return false;
};

/**
 * トリガーシステムの状態をログ出力
 */
export const debugTriggerSystem = (): void => {
  console.log('=== Trigger System Debug ===');
  console.log('Registered Triggers:', getTriggerStats());
  console.log('Total Triggers:', globalRegistry.triggers.size);

  globalRegistry.triggers.forEach((triggerArray, triggerType) => {
    if (triggerArray.length > 0) {
      console.log(`\n${triggerType} (${triggerArray.length}):`);
      triggerArray.forEach((trigger) => {
        console.log(`  - ${trigger.cardName} (Owner: P${trigger.owner}, Slot: ${trigger.slotIndex})`);
      });
    }
  });
};
